import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import studentService from '../../services/api/student';
import { useSettings } from '../../contexts/SettingsContext';
import ExploreCoursesCard from '../../components/cards/ExploreCoursesCard';
import { 
  BookOpen, 
  Search,
  GraduationCap,
  AlertCircle,
  XCircle
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { useGenericSearch } from '../../hooks/useSearch';
import {
  getLessonDuration,
  formatDurationDisplay
} from '../../utils/videoUtils';

const ExploreCourses = () => {
  const navigate = useNavigate();
  const { openWhatsApp } = useSettings();
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const toast = useToast();
  const { user } = useAuth();

  // Debounce filter changes for better performance and avoiding race conditions
  const [debouncedSubject, setDebouncedSubject] = useState(selectedSubject);
  const [debouncedGrade, setDebouncedGrade] = useState(selectedGrade);

  // Debounce subject filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSubject(selectedSubject);
    }, 300); // Debounce 300ms

    return () => clearTimeout(timer);
  }, [selectedSubject]);

  // Debounce grade filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedGrade(selectedGrade);
    }, 300); // Debounce 300ms

    return () => clearTimeout(timer);
  }, [selectedGrade]);

  // دالة لتحويل الأيام إلى نص مفهوم للمدة الاشتراك
  const getDurationTextFromDays = (days) => {
    if (!days) return "اشتراك شهرياً"; // قيمة افتراضية للتوافق
    
    if (days === 30) return "اشتراك شهرياً";
    if (days === 120) return "اشتراك ترم دراسي";
    if (days === 300) return "اشتراك عام دراسي";
    
    // fallback لأي قيمة غير متوقعة
    if (days < 30) return `${days} يوم`;
    if (days % 30 === 0) return `${days / 30} شهور`;
    
    return `${days} يوم`;
  };

  // دالة حساب إجمالي مدة الكورس (ترجع الثواني)
  const calculateTotalCourseDuration = useMemo(() => {
    return (course) => {
      if (!course) return 0;
      
      // أولوية: استخدام total_duration_seconds من API
      if (course.total_duration_seconds && parseInt(course.total_duration_seconds) > 0) {
        return parseInt(course.total_duration_seconds); // بالثواني مباشرة
      }
      
      // ثانوية: استخدام حقول المدة المباشرة من الكورس
      if (course.duration_hours || course.duration_minutes) {
        const hours = parseInt(course.duration_hours) || 0;
        const minutes = parseInt(course.duration_minutes) || 0;
        return (hours * 3600) + (minutes * 60); // تحويل لثواني
      }
      
      // ثالثية: استخدام حقل duration_seconds
      if (course.duration_seconds) {
        return course.duration_seconds; // بالثواني مباشرة
      }
      
      // رابعة: حساب من الدروس
      if (course.lessons && course.lessons.length > 0) {
        const totalSeconds = course.lessons.reduce((total, lesson) => {
          return total + (getLessonDuration(lesson) || 0); // getLessonDuration يرجع seconds
        }, 0);
        return totalSeconds; // ترجع الثواني
      }
      
      return 0;
    };
  }, []);

  // دالة لتنسيق عرض المدة الإجمالية
  const formatTotalDuration = useMemo(() => {
    return (course) => {
      if (!course) return '--:--';
      const totalSeconds = calculateTotalCourseDuration(course);
      return formatDurationDisplay(totalSeconds); // تمرر الثواني مباشرة
    };
  }, [calculateTotalCourseDuration]);

  // جلب الكورسات المتاحة بناءً على الفلاتر
  const { data: coursesData, isLoading, error, refetch } = useQuery(
    ['available-courses', debouncedSubject, debouncedGrade],
    ({ signal }) => studentService.getCourses({
      subjectId: debouncedSubject,
      gradeId: debouncedGrade
    }, signal),
    { 
      keepPreviousData: true,
      // ✅ Retry configuration for handling temporary server errors
      retry: (failureCount, error) => {
        // Don't retry cancelled requests (normal when filters change)
        if (error?.code === 'ERR_CANCELED') return false;
        // Retry on 5xx errors and 429 max 3 times
        const status = error?.response?.status;
        return [500, 502, 503, 429, 408].includes(status) && failureCount < 3;
      }
    }
  );

  // معالجة الكورسات وإضافة المدة المحسوبة من الـ API fallback
  const processedCourses = useMemo(() => {
    const rawCourses = coursesData?.data?.courses || coursesData?.data || coursesData || [];
    return rawCourses.map(course => ({
      ...course,
      // تأكد من وجود الحقول الأساسية
      title: course.title || 'كورس بدون عنوان',
      description: course.description || 'لا يوجد وصف',
      subject_name: course.subject_name || 'مادة غير محددة',
      grade_name: course.grade_name || 'صف غير محدد',
      teacher_name: course.teacher_name || 'مدرس غير محدد',
      student_count: course.student_count || 0,
      average_rating: course.average_rating || 5.0,
      lesson_count: course.lesson_count || 0,
      
      // استخراج المدة من الدروس إذا لم تكن موجودة
      total_duration: course.total_duration || calculateTotalCourseDuration(course),
      formatted_duration: course.formatted_duration || formatTotalDuration(course)
    }));
  }, [coursesData, calculateTotalCourseDuration, formatTotalDuration]);

  // جلب بيانات الفلاتر (المواد والصفوف)
  const { data: filtersData } = useQuery('course-filters', async () => {
    const filters = await studentService.getCourseFilters();
    
    // Extract arrays from response data
    const subjects = filters?.subjects || [];
    // Extract all grades from all stages
    const allGrades = [];
    if (filters?.stages) {
      filters.stages.forEach(stage => {
        if (stage.grades && Array.isArray(stage.grades)) {
          allGrades.push(...stage.grades);
        }
      });
    }
    
    return { subjects, grades: allGrades };
  });

  // Extract arrays from response data
  const filters = filtersData || { subjects: [], grades: [] };

  // Fetch lessons for each listed course (to compute analytics)
  const { data: coursesWithLessons } = useQuery(
    ['available-courses-with-lessons', coursesData?.data?.courses?.length],
    async () => {
      const rawCourses = coursesData?.data?.courses || coursesData?.data || coursesData || [];
      if (!rawCourses || rawCourses.length === 0) return [];

      const promises = rawCourses.map(async (course) => {
        try {
          const res = await studentService.getCourseLessons(course.id);
          const lessons = res.data?.lessons || res.data || [];
          return { ...course, lessons };
        } catch (err) {
          return { ...course, lessons: [] };
        }
      });

      return Promise.all(promises);
    },
    {
      enabled: !!coursesData,
      retry: false,
      staleTime: 60 * 1000
    }
  );


  // تنفيذ عملية الاشتراك (تحويل إلى واتساب)
  const handleSubscribe = (courseId) => {
    // البحث عن بيانات الكورس من البيانات المتاحة
    const courseData = searchedCourses?.find(course => course.id === courseId);
    
    if (!courseData) {
      toast.error('بيانات الكورس غير متاحة');
      return;
    }
    
    // إنشاء رسالة واتساب
    const subjectName = courseData.subject_name || 'مادة غير محدد';
    const courseTitle = courseData.title || 'كورس غير محدد';
    const teacherName = courseData.teacher_name || 'مدرس غير محدد';
    const courseGrade = courseData.grade_name || 'صف غير محدد';
    const price = courseData.price_per_month || 99.99;
    const studentGrade = user?.gradeName || 'صف الطالب غير محدد';
    const durationText = getDurationTextFromDays(courseData.default_duration_days);
    
    const message = (
      `📚 طلب اشتراك في كورس\n\n` +
      `📖 المادة: ${subjectName}\n` +
      `📚 الكورس: ${courseTitle}\n` +
      `👨‍🏫 المدرس: ${teacherName}\n` +
      `🎓 الصف الدراسي للكورس: ${courseGrade}\n` +
      `🎓 الصف الدراسي للطالب: ${studentGrade}\n` +
      `💰 السعر: ${price} جنية ${durationText ? `(${durationText})` : ''}\n\n` +
      `🔸 معرف الطالب: ${user?.id}\n` +
      `🔸 اسم الطالب: ${user?.name}\n` +
      `🔸 البريد الإلكتروني: ${user?.email}\n\n` +
      `يرغب في الاشتراك في هذا الكورس المحدد فقط. يرجى إرسال كود التفعيل بعد تأكيد التحويل.`
    );
    
    // تحويل إلى واتساب باستخدام الإعدادات المركزية
    openWhatsApp(message);
    toast.success('جاري تحويلك إلى واتساب لإتمام عملية الدفع');
  };

  const handleViewCourse = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const resetFilters = () => {
    setHookSearchTerm('');
    setSelectedSubject('');
    setSelectedGrade('');
  };

  // Client-side search using useGenericSearch
  const {
    searchTerm: hookSearchTerm,
    filteredData: searchedCourses,
    setSearchTerm: setHookSearchTerm
  } = useGenericSearch(processedCourses, ['title', 'description', 'subject_name', 'teacher_name', 'grade_name']);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري استكشاف الكورسات المتاحة...</p>
      </div>
    );
  }

  if (error) {
    // Function to determine if error is temporary
    const isTemporaryError = () => {
      const status = error?.response?.status;
      return [503, 502, 500, 408, 429].includes(status) || !error?.response;
    };

    const isNetworkError = !error?.response;
    const status = error?.response?.status;

    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center">
        <div className="bg-red-50 p-10 rounded-[2.5rem] border border-red-100">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">
            {isTemporaryError() ? 'خطأ مؤقت' : 'خطأ في التحميل'}
          </h2>
          
          <p className="text-gray-600 mb-8">
            {isNetworkError 
              ? 'يبدو أن الاتصال بالخادم غير متاح حالياً. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.'
              : status === 503 
              ? 'الخادم مشغول حالياً. يرجى المحاولة بعد قليل.'
              : status === 500 || status === 502
              ? 'حدث خطأ في الخادم. نحن نعمل على إصلاحه.'
              : `حدث خطأ (${status}) أثناء محاولة جلب الكورسات.`
            }
          </p>
          
          <div className="flex gap-3 justify-center flex-wrap">
            {/* ✅ Primary: Smart Retry Button */}
            <button 
              onClick={() => refetch()} 
              className="bg-red-600 text-white px-8 py-3 rounded-2xl font-bold hover:bg-red-700 transition-all active:scale-95"
            >
              {isTemporaryError() ? '🔄 إعادة المحاولة' : '🔄 تحديث الصفحة'}
            </button>
            
            {/* Secondary: Hard Refresh */}
            <button 
              onClick={() => window.location.reload()} 
              className="bg-gray-300 text-gray-900 px-8 py-3 rounded-2xl font-bold hover:bg-gray-400 transition-all active:scale-95"
            >
              🔁 تحديث كامل
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 pt-6 border-t border-red-200 text-left">
              <p className="text-xs text-gray-500 font-mono break-words">
                <strong>Debug:</strong> {error?.message}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-10 px-4 sm:px-6 py-8 bg-gray-50/30 min-h-screen">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight">الكورسات المتاحة</h1>
                    <p className="text-gray-500 mt-2 font-medium flex items-center gap-2">
                        <GraduationCap className="text-primary-600" size={20} />
                        اكتشف أفضل المدرسين وابدأ رحلة نجاحك التعليمي اليوم
                    </p>
                </div>
                <div className="bg-primary-600 text-white px-6 py-3 rounded-2xl shadow-xl shadow-primary-100 flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg"><BookOpen size={20} /></div>
                    <div>
                        <p className="text-[10px] font-bold opacity-80 uppercase">متاح الآن</p>
                        <p className="text-lg font-black">{searchedCourses.length} كورس تعليمي</p>
                    </div>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="mt-10 p-6 bg-gray-50 rounded-[2rem] border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 mr-2">ابحث عن كورس أو مدرس</label>
                        <div className="relative group">
                            <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                            <input
                                type="text"
                                placeholder="اكتب اسم الكورس هنا..."
                                value={hookSearchTerm}
                                onChange={(e) => setHookSearchTerm(e.target.value)}
                                className="w-full pr-12 pl-4 py-3.5 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
                            />
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 mr-2">المادة العلمية</label>
                        <select
                            value={selectedSubject}
                            onChange={(e) => setSelectedSubject(e.target.value)}
                            className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                        >
                            <option value="">كل المواد</option>
                            {filters?.subjects?.map((subject) => (
                                <option key={subject.id} value={subject.id}>{subject.name}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div>
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 mr-2">الصف الدراسي</label>
                        <select
                            value={selectedGrade}
                            onChange={(e) => setSelectedGrade(e.target.value)}
                            className="w-full px-4 py-3.5 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                        >
                            <option value="">كل الصفوف</option>
                            {filters?.grades?.map((grade) => (
                                <option key={grade.id} value={grade.id}>{grade.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Courses Grid Section */}
      {searchedCourses.length === 0 ? (
        <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200 shadow-sm max-w-4xl mx-auto">
          <div className="bg-gray-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
            <Search className="w-12 h-12 text-gray-300" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">لم نجد أي نتائج!</h3>
          <p className="text-gray-500 max-w-sm mx-auto mb-8">
            عذراً، لا توجد كورسات تطابق معايير البحث الحالية. جرب استخدام كلمات بحث أخرى أو قم بتغيير الفلاتر.
          </p>
          <button 
            onClick={resetFilters}
            className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center gap-2 mx-auto"
          >
            عرض كافة الكورسات المتاحة
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {searchedCourses.map((course) => {
            const cw = (coursesWithLessons || []).find(c => c.id === course.id) || course;
            const vids = (cw.lessons || []);
            const courseViews = vids.reduce((sum, v) => sum + (parseInt(v?.views || 0, 10) || 0), 0);

            return (
              <ExploreCoursesCard
                key={course.id}
                course={{ ...course, lessons: cw.lessons || [], courseViews }}
                onViewCourse={handleViewCourse}
                onSubscribe={handleSubscribe}
                formatTotalDuration={formatTotalDuration}
                getDurationTextFromDays={getDurationTextFromDays}
              />
            );
          })}
        </div>
      )}

      {/* Footer Support Banner */}
      <div className="bg-primary-600 rounded-[2.5rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl shadow-primary-200 relative overflow-hidden group">
        <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-white/10 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-700"></div>
        <div className="relative z-10 text-center md:text-right">
            <h3 className="text-2xl font-black mb-2 flex items-center gap-2 justify-center md:justify-start">
                <AlertCircle /> هل واجهت مشكلة في الاشتراك؟
            </h3>
            <p className="text-primary-100 font-medium opacity-90">فريق الدعم الفني متواجد لمساعدتك في تفعيل اشتراكاتك والرد على استفساراتك.</p>
        </div>
        <button 
            onClick={() => navigate('/contact')}
            className="relative z-10 bg-white text-primary-600 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-gray-50 transition-colors"
        >
            تحدث معنا الآن
        </button>
      </div>
    </div>
  );
};

export default ExploreCourses;